import React from 'react'
import { StandardColumn } from '../../../../components/StandardPage'
import { ServicesObjectType } from '../../../../contexts/ServicesContext'
import { getOpCurrentStageLabel, getOpStatusText } from '../utils'
import { stripHtml } from '../../../../utils/stripHtml'
import { OverflowCell } from '../../../../components/OverflowCell'

export const productionOrdersScreenKey = 'production_order'

const asRecord = (service: ServicesObjectType) => service as unknown as Record<string, any>

const formatMaterialsText = (stockInProduction?: any[]): string => {
  if (!stockInProduction || stockInProduction.length === 0) return 'Nenhum'
  const materials = stockInProduction.filter((sip) => sip.Stock != null || sip.stockId != null)
  if (materials.length === 0) return 'Nenhum'
  return materials
    .map((sip) => {
      const name = sip.Stock?.description || sip.Stock?.Code?.code || `Material #${sip.stockId || ''}`
      const qty = sip.usedQuantity ? ` (${sip.usedQuantity} ${sip.usedUnitOfMeasurement || ''})` : ''
      return `${name}${qty}`.trim()
    })
    .join(', ')
}

const renderMaterialsCell = (stockInProduction?: any[]) => {
  if (!stockInProduction || stockInProduction.length === 0) return 'Nenhum'
  const materials = stockInProduction.filter((sip) => sip.Stock != null || sip.stockId != null)
  if (materials.length === 0) return 'Nenhum'
  const inlineText = materials
    .map((sip) => {
      const name = sip.Stock?.description || sip.Stock?.Code?.code || `Material #${sip.stockId || ''}`
      const qty = sip.usedQuantity ? ` (${sip.usedQuantity} ${sip.usedUnitOfMeasurement || ''})` : ''
      return `${name}${qty}`.trim()
    })
    .join(', ')
  const tooltipText = materials
    .map((sip) => {
      const name = sip.Stock?.description || sip.Stock?.Code?.code || `Material #${sip.stockId || ''}`
      const qty = sip.usedQuantity ? ` - ${sip.usedQuantity} ${sip.usedUnitOfMeasurement || ''}` : ''
      return `${name}${qty}`.trim()
    })
    .join('\n')

  return React.createElement(
    OverflowCell,
    { text: tooltipText, maxWidth: 200, forceTooltip: materials.length > 1 },
    inlineText
  )
}

const formatResourcesText = (stockInProduction?: any[]): string => {
  if (!stockInProduction || stockInProduction.length === 0) return 'Nenhum'
  const resources = stockInProduction.filter((sip) => sip.Resource != null || sip.resourceId != null)
  if (resources.length === 0) return 'Nenhum'
  return resources
    .map((sip) => {
      const name = sip.Resource?.name || sip.Resource?.description || sip.Resource?.Code?.code || `Insumo #${sip.resourceId || ''}`
      const qty = sip.usedQuantity ? ` (${sip.usedQuantity} ${sip.usedUnitOfMeasurement || ''})` : ''
      return `${name}${qty}`.trim()
    })
    .join(', ')
}

const renderResourcesCell = (stockInProduction?: any[]) => {
  if (!stockInProduction || stockInProduction.length === 0) return 'Nenhum'
  const resources = stockInProduction.filter((sip) => sip.Resource != null || sip.resourceId != null)
  if (resources.length === 0) return 'Nenhum'
  const inlineText = resources
    .map((sip) => {
      const name = sip.Resource?.name || sip.Resource?.description || sip.Resource?.Code?.code || `Insumo #${sip.resourceId || ''}`
      const qty = sip.usedQuantity ? ` (${sip.usedQuantity} ${sip.usedUnitOfMeasurement || ''})` : ''
      return `${name}${qty}`.trim()
    })
    .join(', ')
  const tooltipText = resources
    .map((sip) => {
      const name = sip.Resource?.name || sip.Resource?.description || sip.Resource?.Code?.code || `Insumo #${sip.resourceId || ''}`
      const qty = sip.usedQuantity ? ` - ${sip.usedQuantity} ${sip.usedUnitOfMeasurement || ''}` : ''
      return `${name}${qty}`.trim()
    })
    .join('\n')

  return React.createElement(
    OverflowCell,
    { text: tooltipText, maxWidth: 200, forceTooltip: resources.length > 1 },
    inlineText
  )
}

const formatServicesText = (serviceStages?: any[]): string => {
  if (!serviceStages || serviceStages.length === 0) return 'Nenhum'
  const items = serviceStages
    .map((ss) => ss.Stage?.name || ss.description || `Etapa #${ss.stageId || ''}`)
    .filter(Boolean)
  return items.length > 0 ? items.join(', ') : 'Nenhum'
}

const renderServicesCell = (serviceStages?: any[]) => {
  if (!serviceStages || serviceStages.length === 0) return 'Nenhum'
  const items = serviceStages
    .map((ss) => ss.Stage?.name || ss.description || `Etapa #${ss.stageId || ''}`)
    .filter(Boolean)
  if (items.length === 0) return 'Nenhum'
  const inlineText = items.join(', ')
  const tooltipText = items.join('\n')

  return React.createElement(
    OverflowCell,
    { text: tooltipText, maxWidth: 200, forceTooltip: items.length > 1 },
    inlineText
  )
}

export const buildProductionOrderColumns = (): StandardColumn<ServicesObjectType>[] => [
  {
    id: 'codeOfOP',
    label: 'OP',
    accessor: (service) => asRecord(service).codeOfOP || '',
    cell: (service) => asRecord(service).codeOfOP || 'N/A',
  },
  {
    id: 'item',
    label: 'Item',
    accessor: (service) =>
      asRecord(service).OrderItem?.Stock?.Code?.code ||
      stripHtml(asRecord(service).OrderItem?.ploomesProductName) ||
      '',
    cell: (service) =>
      asRecord(service).OrderItem?.Stock?.Code?.code ||
      stripHtml(asRecord(service).OrderItem?.ploomesProductName) ||
      'N/A',
  },
  {
    id: 'quantity',
    label: 'Quantidade',
    accessor: (service) => asRecord(service).OrderItem?.quantity ?? asRecord(service).OrderItem?.quantityPieces ?? 1,
    cell: (service) => asRecord(service).OrderItem?.quantity ?? asRecord(service).OrderItem?.quantityPieces ?? '1',
  },
  {
    id: 'materials',
    label: 'Materiais',
    accessor: (service) => formatMaterialsText(asRecord(service).StockInProduction),
    cell: (service) => renderMaterialsCell(asRecord(service).StockInProduction),
    exportValue: (service) => formatMaterialsText(asRecord(service).StockInProduction),
  },
  {
    id: 'resources',
    label: 'Insumos',
    accessor: (service) => formatResourcesText(asRecord(service).StockInProduction),
    cell: (service) => renderResourcesCell(asRecord(service).StockInProduction),
    exportValue: (service) => formatResourcesText(asRecord(service).StockInProduction),
  },
  {
    id: 'services',
    label: 'Serviços',
    accessor: (service) => formatServicesText(asRecord(service).Service_Stage),
    cell: (service) => renderServicesCell(asRecord(service).Service_Stage),
    exportValue: (service) => formatServicesText(asRecord(service).Service_Stage),
  },
  {
    id: 'order',
    label: 'Pedido',
    accessor: (service) =>
      asRecord(service).OrderItem?.Order?.saleNumberPloomes || asRecord(service).OrderItem?.Order?.id || 0,
    cell: (service) =>
      asRecord(service).OrderItem?.Order?.saleNumberPloomes || asRecord(service).OrderItem?.Order?.id || 'N/A',
  },
  {
    id: 'weight',
    label: 'Peso',
    accessor: (service) => asRecord(service).OrderItem?.weight ?? 0,
    cell: (service) => asRecord(service).OrderItem?.weight ?? 'N/A',
  },
  {
    id: 'status',
    label: 'Status',
    accessor: (service) => getOpStatusText(asRecord(service).status),
    cell: (service) => getOpStatusText(asRecord(service).status),
  },
  {
    id: 'currentStage',
    label: 'Etapa atual',
    accessor: (service) => getOpCurrentStageLabel(asRecord(service).Service_Stage || []),
    cell: (service) => getOpCurrentStageLabel(asRecord(service).Service_Stage || []),
  },
  {
    id: 'client',
    label: 'Cliente',
    accessor: (service) =>
      asRecord(service).OrderItem?.Order?.Company_Order_clientIdToCompany?.companyName || '',
    cell: (service) =>
      asRecord(service).OrderItem?.Order?.Company_Order_clientIdToCompany?.companyName
        ?.split(' ')
        .slice(0, 2)
        .join(' ') || 'Cliente não informado',
  },
]
